import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CalendarClock, Send, Loader2, Settings2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errorMessage } from "../lib/api";

const inputCls = "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary";
const toLocal = (iso) => {
  if (!iso) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}`;
};
const fromLocal = (v) => v || null; // orario naive = Europe/Rome (interpretato dal backend)

export const FollowUpComposer = ({ item, open, onClose, onSent }) => {
  const [form, setForm] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [gcal, setGcal] = useState(true);
  const [copia, setCopia] = useState(true);

  useEffect(() => {
    if (!item) return;
    setForm({ cliente_nome: item.cliente_nome || "", cliente_email: item.cliente_email || "", oggetto: item.oggetto, corpo: item.corpo,
      appuntamento: item.appuntamento ? { ...item.appuntamento, inizioLocal: toLocal(item.appuntamento.inizio) } : null });
    api.get("/mail/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ configurato: false }));
  }, [item]);

  if (!item || !form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setAp = (k) => (e) => setForm((f) => ({ ...f, appuntamento: { ...f.appuntamento, [k]: e.target.value } }));
  const isApp = item.tipo === "appuntamento";

  const send = async () => {
    setSending(true);
    try {
      const patch = { cliente_nome: form.cliente_nome, cliente_email: form.cliente_email, oggetto: form.oggetto, corpo: form.corpo };
      if (isApp && form.appuntamento) {
        patch.appuntamento = { inizio: fromLocal(form.appuntamento.inizioLocal), durata_min: Number(form.appuntamento.durata_min) || 30,
          titolo: form.appuntamento.titolo || "Appuntamento", luogo: form.appuntamento.luogo || null, modalita: form.appuntamento.modalita || null };
      }
      await api.patch(`/followups/${item.id}`, patch);
      const { data } = await api.post(`/followups/${item.id}/send`, { google_calendar: gcal, copia_a_me: copia });
      toast.success(isApp ? "Invito inviato" : "Email inviata", { description: `A ${data.sent_to.join(", ")}` });
      if (data.google_calendar_errore) toast.warning("Google Calendar", { description: data.google_calendar_errore });
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Invio non riuscito"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="followup-composer" className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{isApp ? <CalendarClock className="h-5 w-5 text-[#FCD990]" /> : <Mail className="h-5 w-5 text-primary" />}{isApp ? "Conferma appuntamento" : "Scrivi email al cliente"}</DialogTitle>
          <DialogDescription>{item.motivo || "Bozza preparata dall'AI a partire dalla conversazione: rivedila e invia."}</DialogDescription>
        </DialogHeader>
        {settings && !settings.configurato && (
          <div data-testid="composer-no-smtp" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            <span className="inline-flex items-center gap-2"><Settings2 className="h-4 w-4" /> Server di posta non configurato: puoi preparare la mail ma non inviarla.</span>
            <Link to="/impostazioni" data-testid="composer-goto-settings" className="font-semibold underline">Configura ora</Link>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-muted-foreground">Nome cliente
            <input data-testid="composer-nome" className={`${inputCls} mt-1`} value={form.cliente_nome} onChange={set("cliente_nome")} placeholder="Nome e cognome" />
          </label>
          <label className="text-xs text-muted-foreground">Email cliente *
            <input data-testid="composer-email" type="email" className={`${inputCls} mt-1 ${!form.cliente_email ? "border-amber-500/50" : ""}`} value={form.cliente_email} onChange={set("cliente_email")} placeholder="cliente@azienda.it" />
          </label>
        </div>
        {isApp && form.appuntamento && (
          <div data-testid="composer-appuntamento" className="grid gap-3 rounded-lg border border-[#F5B942]/30 bg-[#F5B942]/5 p-3 sm:grid-cols-4">
            <label className="text-xs text-muted-foreground sm:col-span-2">Quando
              <input data-testid="composer-ap-inizio" type="datetime-local" className={`${inputCls} mt-1`} value={form.appuntamento.inizioLocal} onChange={setAp("inizioLocal")} />
            </label>
            <label className="text-xs text-muted-foreground">Durata (min)
              <input data-testid="composer-ap-durata" type="number" min={5} step={5} className={`${inputCls} mt-1`} value={form.appuntamento.durata_min} onChange={setAp("durata_min")} />
            </label>
            <label className="text-xs text-muted-foreground">Modalità
              <select data-testid="composer-ap-modalita" className={`${inputCls} mt-1`} value={form.appuntamento.modalita || ""} onChange={setAp("modalita")}>
                <option value="">—</option><option value="telefono">Telefono</option><option value="video">Video</option><option value="presenza">In presenza</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-2">Titolo evento
              <input data-testid="composer-ap-titolo" className={`${inputCls} mt-1`} value={form.appuntamento.titolo || ""} onChange={setAp("titolo")} />
            </label>
            <label className="text-xs text-muted-foreground sm:col-span-2">Luogo / link
              <input data-testid="composer-ap-luogo" className={`${inputCls} mt-1`} value={form.appuntamento.luogo || ""} onChange={setAp("luogo")} placeholder="Indirizzo o link videocall" />
            </label>
          </div>
        )}
        <label className="text-xs text-muted-foreground">Oggetto
          <input data-testid="composer-oggetto" className={`${inputCls} mt-1`} value={form.oggetto} onChange={set("oggetto")} />
        </label>
        <label className="text-xs text-muted-foreground">Testo
          <textarea data-testid="composer-corpo" rows={10} className={`${inputCls} mt-1 resize-y leading-relaxed`} value={form.corpo} onChange={set("corpo")} />
        </label>
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <label className="inline-flex items-center gap-2"><input data-testid="composer-copia" type="checkbox" checked={copia} onChange={(e) => setCopia(e.target.checked)} /> Mandami una copia</label>
          {isApp && settings?.google_calendar && (
            <label className="inline-flex items-center gap-2"><input data-testid="composer-gcal" type="checkbox" checked={gcal} onChange={(e) => setGcal(e.target.checked)} /> <Calendar className="h-3.5 w-3.5" /> Aggiungi al mio Google Calendar</label>
          )}
          {isApp && settings && !settings.google_calendar && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Invito .ics allegato{settings.google_configurato ? " · " : ""}{settings.google_configurato && <Link to="/impostazioni" className="underline">collega Google Calendar</Link>}</span>}
        </div>
        <div className="flex justify-end gap-2">
          <button data-testid="composer-cancel" onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm hover:border-primary">Annulla</button>
          <button data-testid="composer-send" onClick={send} disabled={sending || !form.cliente_email || !settings?.configurato}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 spin-slow" /> : <Send className="h-4 w-4" />} {isApp ? "Invia conferma e invito" : "Invia email"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
